import { Link, useNavigate } from "@tanstack/react-router";
import { Check } from "lucide-react";
import { JerseyNumber } from "../../components/rink/JerseyNumber";
import { Button } from "../../components/ui/Button";
import { Badge } from "../../components/ui/feedback";
import { Menu, type MenuItem } from "../../components/ui/Menu";
import { Status } from "../../components/ui/Status";
import { useI18n, type Lang } from "../../i18n/I18nProvider";
import type { Configuration, ConfigurationChanges } from "../../lib/api-types";
import { formatRelativeTime } from "../../lib/time";

export interface ConfigRowProps {
  workspaceId: string;
  configuration: Configuration;
  changes: ConfigurationChanges | undefined;
  /** Slots in the lineup, once loaded. */
  playerCount: number | undefined;
  busy: boolean;
  exporting: boolean;
  onToggleLock: () => void;
  onRename: () => void;
  onDuplicate: () => void;
  onDownload: () => void;
  onDelete: () => void;
  onRestore: () => void;
}

/** One configuration as a label-grid row: name, players, lock, download state, one action and a menu. */
export function ConfigRow(props: ConfigRowProps) {
  const { t, lang } = useI18n();
  const navigate = useNavigate();
  const { configuration: config, workspaceId } = props;

  if (config.deletedAt !== null) {
    return (
      <li className="flex items-center gap-3 py-3">
        <span className="min-w-0 flex-1 truncate font-display text-[1.25rem] leading-none font-extrabold uppercase tracking-[0.01em] text-ink-2">
          {config.name}
        </span>
        <Badge tone="muted">{t("common.deleted")}</Badge>
        <Button onClick={props.onRestore}>{t("common.restore")}</Button>
      </li>
    );
  }

  const openLineup = () =>
    void navigate({
      to: "/app/$workspaceId/configurations/$configurationId",
      params: { workspaceId, configurationId: config.id },
    });
  const menuItems: MenuItem[] = [
    { label: t("common.rename"), onSelect: props.onRename, disabled: props.busy },
    { label: t("common.duplicate"), onSelect: props.onDuplicate, disabled: props.busy },
    { label: t("export.button"), onSelect: props.onDownload, disabled: props.exporting },
    {
      label: t("tabs.keys"),
      onSelect: () =>
        void navigate({
          to: "/app/$workspaceId/configurations/$configurationId/keys",
          params: { workspaceId, configurationId: config.id },
        }),
    },
    {
      label: config.locked ? t("configs.unlock") : t("configs.lockForGame"),
      onSelect: props.onToggleLock,
      disabled: props.busy,
    },
    { label: t("common.delete"), onSelect: props.onDelete, tone: "danger" },
  ];

  return (
    <li>
      <article
        aria-label={config.name}
        className="flex flex-wrap items-center gap-x-6 gap-y-2 py-3 sm:flex-nowrap sm:gap-x-5"
      >
        <Link
          to="/app/$workspaceId/configurations/$configurationId"
          params={{ workspaceId, configurationId: config.id }}
          className="min-w-0 flex-1 truncate font-display text-[1.25rem] leading-none font-extrabold uppercase tracking-[0.01em] text-ink hover:text-blue"
        >
          {config.name}
        </Link>
        <div className="order-last flex w-full flex-wrap items-center gap-x-5 gap-y-2 sm:order-none sm:w-auto">
          <span className="flex items-center gap-2">
            <JerseyNumber value={props.playerCount ?? "—"} size="sm" />
            <span className="text-note text-ink-2">{t("configs.players")}</span>
          </span>
          <Status tone={config.locked ? "live" : "idle"}>
            {config.locked ? t("common.locked") : t("configs.open")}
          </Status>
          <DownloadState changes={props.changes} now={new Date()} lang={lang} />
        </div>
        <div className="ml-auto flex items-center gap-2">
          <Button variant="secondary" onClick={openLineup}>
            {t("configs.openLineup")}
          </Button>
          <Menu label={t("configs.more", { name: config.name })} items={menuItems} />
        </div>
      </article>
    </li>
  );
}

interface DownloadStateProps {
  changes: ConfigurationChanges | undefined;
  now: Date;
  lang: Lang;
}

/** Never downloaded (ice), changed since the last download (red wash), or up to date (blue check). */
function DownloadState({ changes, now, lang }: DownloadStateProps) {
  const { t } = useI18n();
  if (!changes) return null;
  if (changes.lastDownloadedAt === null) {
    return <Badge tone="muted">{t("configs.neverDownloaded")}</Badge>;
  }
  if (changes.changedCount > 0) {
    return (
      <Badge tone="accent">{t("configs.changedSince", { count: changes.changedCount })}</Badge>
    );
  }
  return (
    <span className="inline-flex items-center gap-1.5 text-note font-semibold text-blue">
      <Check aria-hidden="true" className="size-[18px]" strokeWidth={1.75} />
      {t("configs.downloadedAt", {
        when: formatRelativeTime(changes.lastDownloadedAt, now, lang),
      })}
    </span>
  );
}
